import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useAbility } from './AbilityContext';
import { TOUR_STEPS, type TourStep } from '../components/tour/tourSteps';
import { useGetVendorMeQuery, useUpdateVendorMutation } from '../store/vendorApi';

interface ProductTourContextType {
  isActive: boolean;
  currentStepIndex: number;
  currentStep: TourStep | null;
  availableSteps: TourStep[];
  totalSteps: number;
  startTour: () => void;
  nextStep: () => void;
  previousStep: () => void;
  skipTour: () => void;
  endTour: () => void;
  restartTour: () => void;
}

const ProductTourContext = createContext<ProductTourContextType | undefined>(undefined);

export const useProductTour = () => {
  const context = useContext(ProductTourContext);
  if (!context) {
    throw new Error('useProductTour must be used within ProductTourProvider');
  }
  return context;
};

export const ProductTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isTeamMember } = useAuth();
  const ability = useAbility();
  const { data: vendor } = useGetVendorMeQuery(undefined, { skip: isTeamMember });
  const [updateVendor] = useUpdateVendorMutation();

  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasAutoStarted, setHasAutoStarted] = useState(false);

  // Filter steps based on permissions and role
  const availableSteps = useMemo(() => {
    return TOUR_STEPS.filter((step) => {
      if (step.vendorOnly && isTeamMember) return false;
      if (step.permission && !ability.can(step.permission.action, step.permission.subject)) return false;
      return true;
    });
  }, [ability, isTeamMember]);

  const currentStep = isActive ? availableSteps[currentStepIndex] ?? null : null;

  const startTour = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(true);
    if (availableSteps.length > 0) {
      navigate(availableSteps[0].route);
    }
  }, [availableSteps, navigate]);

  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentStepIndex(0);
    if (vendor?.email) {
      updateVendor({ email: vendor.email, tourCompleted: true }).catch(() => {
        // Silently ignore — tour state will re-sync on next load
      });
    }
    navigate('/dashboard');
  }, [vendor?.email, updateVendor, navigate]);

  const skipTour = useCallback(() => {
    endTour();
  }, [endTour]);

  const nextStep = useCallback(() => {
    if (currentStepIndex < availableSteps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);
      navigate(availableSteps[nextIndex].route);
    } else {
      endTour();
    }
  }, [currentStepIndex, availableSteps, navigate, endTour]);

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevIndex = currentStepIndex - 1;
      setCurrentStepIndex(prevIndex);
      navigate(availableSteps[prevIndex].route);
    }
  }, [currentStepIndex, availableSteps, navigate]);

  const restartTour = useCallback(() => {
    if (vendor?.email) {
      updateVendor({ email: vendor.email, tourCompleted: false }).catch(() => {
        // Silently ignore
      });
    }
    setHasAutoStarted(false);
    startTour();
  }, [vendor?.email, updateVendor, startTour]);

  // Sync step index when user navigates with browser back/forward
  useEffect(() => {
    if (!isActive) return;
    const matchingIndex = availableSteps.findIndex(
      (step) => location.pathname === step.route || location.pathname.startsWith(step.route + '/')
    );
    if (matchingIndex !== -1 && matchingIndex !== currentStepIndex) {
      setCurrentStepIndex(matchingIndex);
    }
  }, [location.pathname, isActive, availableSteps, currentStepIndex]);

  useEffect(() => {
    if (hasAutoStarted || isActive) return;
    if (!user?.id) return;
    if (vendor?.tourCompleted === true) return;
    if (location.pathname !== '/dashboard') return;

    setHasAutoStarted(true);
    // Small delay so dashboard renders first
    const timer = setTimeout(() => {
      setIsActive(true);
      setCurrentStepIndex(0);
    }, 800);
    return () => clearTimeout(timer);
  }, [user?.id, vendor?.tourCompleted, location.pathname, hasAutoStarted, isActive]);

  const value = useMemo(
    () => ({
      isActive,
      currentStepIndex,
      currentStep,
      availableSteps,
      totalSteps: availableSteps.length,
      startTour,
      nextStep,
      previousStep,
      skipTour,
      endTour,
      restartTour,
    }),
    [isActive, currentStepIndex, currentStep, availableSteps, startTour, nextStep, previousStep, skipTour, endTour, restartTour]
  );

  return (
    <ProductTourContext.Provider value={value}>
      {children}
    </ProductTourContext.Provider>
  );
};
